import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import getApplicationFormFields, {
  APPLICATION_FORM_TYPE_SR_APPLICATION_CREDIT_INFO,
  virtualFormFieldIds,
} from 'common/householdApplication/applicationFormFields';

import {
  RENTAL_APPLICATION_STEP_PERSONAL_INFO,
  RENTAL_APPLICATION_STEP_FORM,
} from 'common/householdApplication/rentalApplicationHelpers';

import {
  SR_API_ACTION_HOUSEHOLD_APPLICATION_SHOULD_GO_TO_CHECKOUT,
  SR_API_ACTION_HOUSEHOLD_APPLICATION_PROCESS_CHARGE,
} from 'api/actionTypes';

import { createFormValidationFn } from 'client/helpers/formikHelpers';
import { getListingFullAddressString } from 'common/listingHelpers';
import { LISTING_STATUS_ACTIVE } from 'common/listingConstants';
import {
  getHouseholdApplicationDataFields,
  getApplicationFeeUsd,
  getApplicationLimit,
} from 'common/householdApplication/applicationHelpers';
import { STRIPE_ERROR_CODES_HANDLED } from 'common/stripeConstants';
import { getStripeCardErrorMessage } from 'common/stripeHelpers';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrSeparator from 'client/components/SrSeparator';
import SrStripeCardForm from 'client/components/SrStripeCardForm';
import ActivityIndicator from 'client/components/ActivityIndicator';
import SrRentersApplyingToListingAlert from 'client/components/SrRentersApplyingToListingAlert';
import SrRentalApplicationFlowStepHeader from 'client/components/SrRentalApplicationFlowStepHeader';

import branch from 'client/core/branch';
import withApiState from 'client/helpers/withApiState';
import processChargeAction from 'client/actions/HouseholdApplication/processCharge';

const getShouldDisableStripePaymentForm = ({
  listing,
  isTenantScreening,
  householdsApplyingToListing,
  guarantorApplicationId,
}) => {
  if (isTenantScreening) {
    return false;
  }

  return (
    householdsApplyingToListing &&
    !guarantorApplicationId &&
    (householdsApplyingToListing.tentativeQualifiedAndProcessing.excludingCurrentHousehold >=
      getApplicationLimit(listing) || // eslint-disable-line max-len
      householdsApplyingToListing.accepted.excludingCurrentHousehold >= 1)
  );
};

@withApiState({
  householdApplicationProcessChargeApiState: {
    action: SR_API_ACTION_HOUSEHOLD_APPLICATION_PROCESS_CHARGE,
  },
  householdApplicationShouldBeChargedApiState: {
    action: SR_API_ACTION_HOUSEHOLD_APPLICATION_SHOULD_GO_TO_CHECKOUT,
  },
})
@branch({
  householdsApplyingToListingByListingId: ['householdsApplyingToListingByListingId'],
})
class SrRentalApplicationFlowStepPay extends Component {
  static propTypes = {
    user: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    listing: PropTypes.object.isRequired,
    renterProfile: PropTypes.object.isRequired,
    isTenantScreening: PropTypes.bool,
    tenantAlertReport: PropTypes.object,
    guarantorApplicationId: PropTypes.number,
    householdApplicationId: PropTypes.number,
    goToNextRentalApplicationStep: PropTypes.func.isRequired,
    getCurrentRentalApplicationUrl: PropTypes.func.isRequired,
    householdsApplyingToListingByListingId: PropTypes.object.isRequired,
    householdApplicationProcessChargeApiState: PropTypes.object.isRequired,
    householdApplicationShouldBeChargedApiState: PropTypes.object.isRequired,
  };

  _onPaymentFormSubmit = (source) => {
    const {
      listing,
      householdApplicationId,
      guarantorApplicationId,
      dispatch,
      goToNextRentalApplicationStep,
    } = this.props;

    return dispatch(processChargeAction, {
      source,
      listingId: listing.id,
      householdApplicationId: householdApplicationId || guarantorApplicationId,
      onSuccess: () => {
        goToNextRentalApplicationStep(RENTAL_APPLICATION_STEP_FORM);
      },
    });
  };

  _handleErrorMessage = () => {
    const { householdApplicationProcessChargeApiState } = this.props;

    if (!householdApplicationProcessChargeApiState.error) {
      return null;
    }

    if (
      !STRIPE_ERROR_CODES_HANDLED.includes(householdApplicationProcessChargeApiState.error.code)
    ) {
      return 'Sorry, the card did not work. Please try another card.';
    }

    return getStripeCardErrorMessage(householdApplicationProcessChargeApiState.error).message;
  };

  render() {
    const {
      listing,
      user,
      renterProfile,
      tenantAlertReport,
      isTenantScreening,
      householdApplicationProcessChargeApiState,
      getCurrentRentalApplicationUrl,
      householdsApplyingToListingByListingId,
      guarantorApplicationId,
      householdApplicationId,
      householdApplicationShouldBeChargedApiState,
    } = this.props;

    const formValues = getHouseholdApplicationDataFields(renterProfile);
    const formFields = getApplicationFormFields({
      formType: APPLICATION_FORM_TYPE_SR_APPLICATION_CREDIT_INFO,
      formValues: formValues,
    }).filter((formField) => !virtualFormFieldIds.includes(formField.id));

    const formValidationErrors = createFormValidationFn(formFields)(formValues);
    delete formValidationErrors.ssn;

    // The renter should only be allowed to pay if there are no errors in the personal info form
    if (Object.keys(formValidationErrors).length > 0) {
      return (
        <Redirect to={getCurrentRentalApplicationUrl(RENTAL_APPLICATION_STEP_PERSONAL_INFO)} />
      );
    }

    const applicationFeeUsd = getApplicationFeeUsd({
      applicationFee: listing.applicationFee,
      user,
    });
    const householdsApplyingToListing = householdsApplyingToListingByListingId[listing.id];
    const shouldDisableStripePaymentForm = getShouldDisableStripePaymentForm({
      listing,
      isTenantScreening,
      householdsApplyingToListing,
      guarantorApplicationId,
    });

    const errorMessage = this._handleErrorMessage();

    const contentDOM = (
      <div>
        <SrText className="pageHeadline" size="2xl" weight="medium" leading="2xl">
          Pay ${applicationFeeUsd} application fee for
          <SrText size="2xl" weight="bold" leading="2xl">
            {` ${getListingFullAddressString(listing)}`}
          </SrText>
        </SrText>
        <SrSeparator />
        <SrText>
          {'Each applicant age 18 or older must pay an application fee. '}
          {'You can use your credit or debit card to securely pay. '}
          {'This payment is non-refundable, and will cover any additional '}
          {'listings you apply to for 60 days.'}
        </SrText>
        <SrStripeCardForm
          onSubmit={this._onPaymentFormSubmit}
          isSubmitting={householdApplicationProcessChargeApiState.inProgress}
          submitErrorMessage={errorMessage}
          buttonContainerClassName="SrStaticFooter size-135"
          buttonTitle={
            shouldDisableStripePaymentForm
              ? 'Temporarily Disabled'
              : tenantAlertReport
              ? 'Pay Now'
              : 'Pay & Run Credit'
          }
          disabled={!householdsApplyingToListing || shouldDisableStripePaymentForm}
        />
        <SrBox height={10} />
        {listing.status === LISTING_STATUS_ACTIVE && !guarantorApplicationId && (
          <SrRentersApplyingToListingAlert
            householdsApplyingToListing={householdsApplyingToListing}
            listing={listing}
            rounded
          />
        )}
        <SrBox height={23} />
        <SrText color="grey" size="sm" lineHeight="16px">
          By paying below, I hereby authorize credit agencies, law enforcement agencies, city,
          state, county and federal courts and agencies to release all information they may have
          about me including, but not limited to, credit, collections, criminal, and driving
          history. You acknowledge and agree that you are giving Sunroom Rentals your "written
          instruction" to assess your report, and to make a shared version of your credit report
          available to the property manager and/or landlord of any property you apply to through
          Sunroom Rentals.
        </SrText>
      </div>
    );

    return (
      <div className="RentalApplicationPage">
        <SrRentalApplicationFlowStepHeader
          activeStep={2}
          isGuarantorApp={guarantorApplicationId && !householdApplicationId}
        />
        <div className="main">
          <div className="mainContent">
            {householdApplicationShouldBeChargedApiState.inProgress ? (
              <ActivityIndicator />
            ) : (
              contentDOM
            )}
          </div>
        </div>
      </div>
    );
  }
}

SrRentalApplicationFlowStepPay.displayName = 'SrRentalApplicationFlowStepPay';

export default SrRentalApplicationFlowStepPay;
